import { FormPayload, FormPayloadSyonet } from "@/app/interface/components";
import { normalizeModelName } from "./handleSubmit";
import { sendToToyotaLeadCenter } from "./toyotaLeadCenterIntegration";
import { getPersistedUtmParams } from "@/app/utils/utm";
import {
	buildAdReferralLeadPayload,
	isSyonetEnabled,
	isToyotaBrand,
	redirectToAlanWhatsApp,
	sendToAlan,
} from "./alanIntegration";

const SYONET_DEALER_IDS: { [key: string]: string } = {
	PAMPULHA: "35",
	CONTAGEM: "28",
	"BH-CARANDAI": "27",
	"SETE LAGOAS": "37",
	"UBERLÂNDIA (JOÃO PINHEIRO)": "42",
	"UBERLÂNDIA (JOÃO NAVES)": "43",
	UBERABA: "41",
	"PATOS DE MINAS": "36",
	IPATINGA: "30",
	"GOVERNADOR VALADARES": "29",
	"TEÓFILO OTONI": "39",
	ITABIRA: "31",
	MANHUAÇU: "33",
	"JUIZ DE FORA": "32",
	MURIAÉ: "34",
	UBÁ: "40",
	BARBACENA: "38",
};

// Função auxiliar para determinar o eventType baseado na página
function getEventTypeFromPathname(): string {
	if (typeof window === "undefined") return "NOVOS WEB";

	const pathname = window.location.pathname.toLowerCase();

	if (pathname.includes("/venda-direta")) {
		return "VENDA DIRETA WEB";
	} else if (pathname.includes("/consorcio")) {
		return "CONSORCIO WEB";
	} else if (pathname.includes("/assinatura")) {
		return "ASSINATURA WEB";
	} else if (
		pathname.includes("/pos-venda") ||
		pathname.includes("/servicos")
	) {
		return "SERVICOS WEB";
	}

	return "NOVOS WEB";
}

export async function sendToSyonet(
	formData: Omit<FormPayload, "registrationNumber"> & {
		registrationNumber?: string;
	},
	brand: string,
	options?: {
		skipToyotaLeadCenter?: boolean;
		enableSyonet?: boolean;
		eventType?: string;
		companyId?: string;
		media?: string;
		redirectToWhatsApp?: boolean;
	},
) {
	try {
		// Cast to any to accept frontend-specific fields (nome_loja, categoria, phone)
		const fd: any = formData as any;
		// Accept either 'nome_loja' (frontend) or 'dealer' (legacy) as source for the dealer name
		const dealerName = (fd.nome_loja || fd.dealer || "")
			.toString()
			.toUpperCase();
		const companyId =
			options?.companyId ||
			(fd.companyId != null && String(fd.companyId).trim() !== ""
				? String(fd.companyId)
				: SYONET_DEALER_IDS[dealerName] || "35");
		let modelValue = "Não informado";
		if (formData.model) {
			const modelStr = formData.model.toString();
			if (
				modelStr &&
				modelStr !== "0" &&
				!["NaN", "undefined", "null"].includes(modelStr)
			) {
				modelValue = normalizeModelName(modelStr);
			}
		}

		// Recupera UTMs persistidos, se não houver, tenta pegar da URL atual
		let utmParamsString = getPersistedUtmParams();
		if (!utmParamsString && typeof window !== "undefined") {
			utmParamsString = window.location.search;
		}

		const urlParams = new URLSearchParams(utmParamsString);
		const utmParams = {
			utmcsr: urlParams.get("utm_source") || "",
			utmcmd: urlParams.get("utm_medium") || "Orgânico",
			utmccn: urlParams.get("utm_campaign") || "",
			utmcct: urlParams.get("utm_content") || "",
			utmctm: urlParams.get("utm_term") || "",
		};

		// Determina o eventType: usa o valor customizado passado via options, ou fallback pelo pathname
		const eventType = options?.eventType || getEventTypeFromPathname();
		const media = fd.media || options?.media;

		const syonetPayload: FormPayloadSyonet = {
			formPayload: {
				name: formData.name,
				email: formData.email || "",
				...(formData.registrationNumber !== undefined
					? { document: formData.registrationNumber }
					: {}),
				phone: fd.phone || fd.cellPhone || "",
				companyId: companyId,
				eventType: fd.eventType || eventType,
				model: modelValue,
				hubUtmz: utmParams,
				influencer: "",
				page: formData.page || "",
				comment: (fd.comment || "").toString(),
				...(media ? { media: String(media) } : {}),
				isChat: false,
			},
			brand: brand.toUpperCase(),
			isChat: false,
		};

		if (!options?.skipToyotaLeadCenter) {
			sendToToyotaLeadCenter({
				name: formData.name,
				email: formData.email || "",
				phone: fd.phone || fd.cellPhone || "",
				document: formData.registrationNumber,
				brand: brand.toUpperCase(),
				model: modelValue,
				page: formData.page,
				comment: (fd.comment || "").toString(),
				dealer_group: undefined,
				companyId: companyId,
				eventType: eventType,
				utm_source: utmParams.utmcsr || undefined,
				utm_medium: utmParams.utmcmd || undefined,
				utm_campaign: utmParams.utmccn || undefined,
				utm_content: utmParams.utmcct || undefined,
				utm_term: utmParams.utmctm || undefined,
				isChat: false,
				source: "form",
			}).catch(() => {});
		}

		if (isToyotaBrand(brand)) {
			const alanOk = await sendToAlan(
				buildAdReferralLeadPayload({
					name: formData.name,
					email: formData.email || "",
					phone: fd.phone || fd.cellPhone || "",
					model: modelValue,
					dealer: fd.nome_loja || fd.dealer || "",
					page: formData.page,
					externalId: formData.registrationNumber,
					utmParams: utmParamsString,
				}),
			);

			if (!alanOk) {
				if (!options?.enableSyonet) {
					throw new Error("Erro ao enviar para Alan");
				}
				console.warn("Falha no Alan; prosseguindo com o envio ao Syonet.");
			}
		}

		if (isSyonetEnabled() || options?.enableSyonet) {
			const response = await fetch("/api/syonet", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"x-api-key": process.env.API_KEY || "",
				},
				body: JSON.stringify(syonetPayload),
			});
			const responseText = await response.text();

			let responseData;
			try {
				responseData = responseText ? JSON.parse(responseText) : {};
			} catch (parseError) {
				console.error("Erro ao fazer parse da resposta:", parseError);
				responseData = { message: responseText };
			}

			if (!response.ok) {
				console.error("Erro detalhado ao enviar para Syonet:", {
					status: response.status,
					statusText: response.statusText,
					error: responseData,
				});
				throw new Error(
					`Erro ao enviar para Syonet: ${JSON.stringify(responseData)}`,
				);
			}
		}

		if (
			isToyotaBrand(brand) &&
			options?.redirectToWhatsApp !== false
		) {
			redirectToAlanWhatsApp(
				formData.name,
				(fd.nome_loja || fd.dealer || "").toString(),
				modelValue,
			);
		}

		return true;
	} catch (error) {
		console.error("Erro detalhado na integração com SYONET WhatsApp:", {
			error,
			message:
				error instanceof Error ? error.message : "Erro desconhecido",
			stack: error instanceof Error ? error.stack : undefined,
		});
		return false;
	}
}

export async function sendToSyonetWhatsapp(
	name: string,
	email: string,
	document: string,
	phone: string,
	dealer: string,
	model: string,
	utmParams?: string,
) {
	try {
		const dealerName = dealer?.toUpperCase() || "";
		const companyId = SYONET_DEALER_IDS[dealerName] || "35";

		let modelValue = "Não informado";
		if (model) {
			const modelStr = model.toString();
			if (
				modelStr &&
				modelStr !== "0" &&
				!["NaN", "undefined", "null"].includes(modelStr)
			) {
				modelValue = normalizeModelName(modelStr);
			}
		}

		let hubUtmz = {
			utmcsr: "",
			utmcmd: "Orgânico",
			utmccn: "",
			utmcct: "",
			utmctm: "",
		};

		// Usa UTMs passados por parâmetro, se não houver, tenta pegar persistidos
		let finalUtmParams = utmParams;
		if (!finalUtmParams) {
			finalUtmParams = getPersistedUtmParams();
		}

		if (finalUtmParams) {
			const urlParams = new URLSearchParams(finalUtmParams);
			hubUtmz = {
				utmcsr: urlParams.get("utm_source") || "",
				utmcmd: urlParams.get("utm_medium") || "Orgânico",
				utmccn: urlParams.get("utm_campaign") || "",
				utmcct: urlParams.get("utm_content") || "",
				utmctm: urlParams.get("utm_term") || "",
			};
		}

		const syonetPayload = {
			formPayload: {
				name: name,
				email: email,
				document: document,
				phone: phone,
				companyId: companyId,
				eventType: getEventTypeFromPathname(),
				model: modelValue,
				hubUtmz: hubUtmz,
				influencer: "whatsapp-site",
			},
			brand: "TOYOTA",
		};

		if (!isSyonetEnabled()) {
			return true;
		}

		const apiKey = process.env.NEXT_PUBLIC_API_KEY;
		const response = await fetch(
			"https://api-pb-landingpage-prd.azurewebsites.net/api/post-data/integration/syonet",
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"x-api-key": apiKey ?? "",
				},
				body: JSON.stringify(syonetPayload),
			},
		);

		const responseData = await response.json();

		if (!response.ok) {
			console.error("Erro detalhado ao enviar para SYONET WhatsApp:", {
				status: response.status,
				statusText: response.statusText,
				error: responseData,
			});
			throw new Error(
				`Erro ao enviar para SYONET WhatsApp: ${JSON.stringify(
					responseData,
				)}`,
			);
		}

		return true;
	} catch (error) {
		console.error("Erro detalhado na integração com SYONET WhatsApp:", {
			error,
			message:
				error instanceof Error ? error.message : "Erro desconhecido",
			stack: error instanceof Error ? error.stack : undefined,
		});
		return false;
	}
}
